import React, { useState, useRef, useEffect } from "react";

import ImageHandler from "../../utils/ImagesHandler";
// import RadioButtonCard from "../Cards/RadioButtonCard";
import ModelSchemas from "../../schemas/model.schema";
import ModelsAPI from "../../API/models.api";
import { ErrorAlert } from "../../utils/Alerts";
import ModelAccess from "./ModelAccess";
import { getUserEmail } from "../../utils/OktaHelpers";
import { Trans, useTranslation } from "react-i18next";
import TitleWithDescription from "../Headers/TitleWithDescription";
import StepsNavButtons from "../Buttons/StepsNavButtons";
import LazyImage from "../Shared/LazyImage";
import ParametersForm from "../Cards/ParametersForm";
import { validate } from "../../utils/ValidationHelpers";
import ValidationError from "../Shared/ValidationError";
import ScannerSettingsForm from "../Forms/ScannerSettingsForm";
import ModelSupportedFeatures from "../PublishModelSteps/ModelSupportedFeatures";
import { MODEL_PROCESSING_TYPES, UNITS } from "../../utils/constants";
import { getEncodedValue } from "../../utils/Helpers";
import { isEmpty } from "lodash";
import Select from "react-select";
import {
    getReactSelectStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import LookupsAPI from "../../API/lookups.api";
import ProductTags from "../Tags/ProductTags";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getRoles } from "../../utils/OktaHelpers";
import { ROLES } from "../../utils/constants";

const ManageModel = ({
    prevStep,
    model,
    setModel,
    submitModel,
    isTesting,
    setUserAccess,
    setOrganizationAccess,
    maxAllowedKFold,
    nextStep,
    isSubmitting,
    setIsSubmitting,
    setIsSubmited,
    parameterNames,
    parametersSettings,
    setParametersSettings,
    scannerSettings,
    setScannerSettings,
    modelOptions,
    setModelOptions,
    isCustom,
    activeUnit,
    setActiveUnit,
    classificationParameters,
    setClassificationParameters,
}) => {
    const [isFormValid, setIsFormValid] = useState(false);
    const [isParametersValid, setIsParametersValid] = useState(false);
    const [isClassificationParamValid, setIsClassificationParamValid] =
        useState(true);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});
    const [parameterError, setParameterError] = useState({});
    const [classificationParamError, setClassificationParamError] = useState(
        {}
    );
    const [modelOptionsError, setModelOptionsError] = useState({});

    const [options, setOptions] = useState({});
    const [doesModelNameExist, setDoesModelNameExist] = useState(false);
    const [existModelName, setExistModelName] = useState("");

    const [isScannerSettingsValid, setIsScannerSettingsValid] = useState(false);
    const [isModelOptionsValid, setIsModelOptionsValid] = useState(false);

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [mappedUserAccesses, setMappedUserAccesses] = useState(null);

    const [selectedOrganizations, setSelectedOrganizations] = useState([]);

    const [classifiersLookup, setClassifiersLookup] = useState([]);

    const [parameterUnitsMaps, setParameterUnitsMaps] = useState({});

    const imageError = useRef(false);
    const { t } = useTranslation();

    const imageInput = useRef(null);
    const [radioData, setRadioData] = useState([
        {
            name: "crossValidation",
            title: t("crossValidation"),
            message: t("crossValidationMessage"),
            options: [
                {
                    name: "One Sample Out",
                    label: t("oneSampleOut"),
                },
                {
                    name: "One Reference Out",
                    label: t("oneReferenceOut"),
                },
                {
                    name: "K-Fold",
                    label: t("kFold"),
                    requireValue: true,
                    minValue: 1,
                },
            ],
            icon: "random",
            required: true,
        },
        {
            name: "testingMethod",
            title: t("testingMethod"),
            message: t("testingMethodMessage"),
            options: [
                {
                    name: "No Testing Set",
                    label: t("noTestingSet"),
                },
                {
                    name: "80/20 Split",
                    label: t("splitPercentage8020"),
                },
                {
                    name: "Separate Testing Set",
                    label: t("separateTestingSet"),
                },
            ],
            icon: "flask",
            required: true,
        },
        {
            name: "modelType",
            title: t("modelType"),
            message: t("modelTypeMessage"),
            options: [
                {
                    name: "Standard",
                    label: t("standard"),
                },
                {
                    name: "Fast",
                    label: t("fast"),
                },
                {
                    name: "Slow",
                    label: t("slow"),
                },
            ],
            icon: "list",
            required: true,
        },
    ]);

    useEffect(() => {
        setOptions(model.options);
        setParametersSettings(
            mapParameters() || [
                {
                    name: "",
                    ...(isCustom && {
                        type: MODEL_PROCESSING_TYPES.Regression,
                    }),
                    localizations: { en: "" },
                    parameterDetails: [
                        {
                            unit: UNITS.percentage,
                            decimalPlaces: 2,
                        },
                    ],
                },
            ]
        );
    }, []);

    useEffect(() => {
        const temp = [...radioData];
        temp[1].options[2].disabled = isTesting;
        setRadioData(temp);
    }, [isTesting]);

    useEffect(() => {
        const temp = [...radioData];
        const kFoldValue =
            model?.options?.crossValidation?.value ||
            Math.ceil(maxAllowedKFold / 2);
        const value =
            model?.options?.crossValidation?.name == "K-Fold"
                ? kFoldValue
                : Math.ceil(maxAllowedKFold / 2);
        temp[0].options[2].maxValue = maxAllowedKFold;
        temp[0].options[2].value = value;
        temp[0].options[2].defaultValue = value;
        setRadioData(temp);
    }, [maxAllowedKFold]);

    useEffect(() => {
        setDoesModelNameExist(false);
        if (model.name === existModelName) {
            setDoesModelNameExist(true);
        }

        validate(
            model,
            ModelSchemas.CreateModelSchema,
            setIsFormValid,
            setError
        );
    }, [model]);

    useEffect(() => {
        validate(
            parametersSettings,
            ModelSchemas.ParametersSettings,
            setIsParametersValid,
            setParameterError
        );
    }, [parametersSettings]);

    useEffect(() => {
        validate(
            modelOptions,
            ModelSchemas.ModelOptionsSchema,
            setIsModelOptionsValid,
            setModelOptionsError
        );
    }, [modelOptions]);

    useEffect(() => {
        if (classificationParameters) {
            validate(
                classificationParameters,
                ModelSchemas.ClassificationParameters,
                setIsClassificationParamValid,
                setClassificationParamError
            );
        }
    }, [classificationParameters]);

    useEffect(() => {
        const mappedAccess = selectedUsers.map((user) => {
            if (user.value.write) {
                return {
                    userId: user.value.id,
                    read: true,
                    write: true,
                };
            } else {
                return {
                    userId: user.value.id,
                    read: true,
                    write: false,
                };
            }
        });
        setUserAccess(mappedAccess);
    }, [selectedUsers]);

    useEffect(() => {
        const mappedAccess = selectedOrganizations.map((organization) => {
            return {
                organizationId: organization.value,
                write: false,
                read: true,
            };
        });

        setOrganizationAccess(mappedAccess);
    }, [selectedOrganizations]);

    useEffect(() => {
        setModel({ ...model, options });
    }, [options]);

    useEffect(() => {
        if (isCustom) getClassifiersLookup();
    }, [isCustom]);

    useEffect(() => {
        const map = {};
        modelOptions.dynamicSortedParameterNames.forEach(
            (parameter) => (map[parameter.value] = parameter.unitName)
        );
        setParameterUnitsMaps(map);
    }, [modelOptions?.dynamicSortedParameterNames]);

    const getClassifiersLookup = async () => {
        const result = await LookupsAPI.getClassifiers();
        setClassifiersLookup(result);
    };

    const handleInputChange = (e) => {
        setModel({
            ...model,
            [e.target.name]: e.target.value,
        });
    };

    const handleImage = async (e) => {
        fileValidation(e.target.value);
        ImageHandler.formatImage(e, (image) => {
            setModel({
                ...model,
                image,
            });
        });
    };

    // const renderRadioButtons = () => {
    //     return (
    //         radioData &&
    //         radioData.map((record, index) => {
    //             return (
    //                 <RadioButtonCard
    //                     key={`radiobutton-option-${index}`}
    //                     options={options}
    //                     setOptions={setOptions}
    //                     message={record.message}
    //                     radioData={record}
    //                     isFormSubmitted={isFormSubmitted}
    //                     error={error}
    //                 />
    //             );
    //         })
    //     );
    // };

    const fileValidation = (path) => {
        var allowedExtensions = /(\.jpg|\.jpeg|\.png)$/i;
        if (!allowedExtensions.exec(path)) {
            ErrorAlert(t("imageExtensionError"));
            imageError.current = true;
        } else imageError.current = false;
    };

    const handleCreateModelClicked = () => {
        setIsSubmitting(true);
        setIsFormSubmitted(true);
        if (
            !isFormValid ||
            !isScannerSettingsValid ||
            !isParametersValid ||
            !isClassificationParamValid ||
            !isModelOptionsValid
        ) {
            setIsSubmitting(false);
            return;
        }

        if (imageError.current) {
            ErrorAlert(t("imageExtensionError"));
            setIsSubmitting(false);
            return;
        }

        const formattedModelName = getEncodedValue(model.name);
        ModelsAPI.doesModelNameExist(formattedModelName)
            .then((doesExists) => {
                if (!doesExists) {
                    submitModel()
                        .then((res) => {
                            if (res) {
                                nextStep();
                                setIsSubmited(res);
                            } else {
                                setIsSubmited(res);
                            }
                        })
                        .finally(() => setIsSubmitting(false));
                    setDoesModelNameExist(false);
                } else {
                    ErrorAlert(t("modelNameExists"));
                    setIsSubmitting(false);
                    setDoesModelNameExist(true);
                    setExistModelName(model?.name);
                }
            })
            .catch(() => {
                setIsSubmitting(false);
            });
    };

    const handleImageValue = (value = null) => {
        setModel({ ...model, image: value });
        if (!value) {
            imageInput.current.value = "";
        }
    };

    const getIsStepValid = () => {
        const validForm =
            !(
                isFormValid &&
                isScannerSettingsValid &&
                isParametersValid &&
                isClassificationParamValid
            ) && isFormSubmitted;
        return validForm || isSubmitting;
    };

    const mapParameters = () => {
        const mappedParameters = parameterNames?.map((parameter) => ({
            name: parameter,
            parameterDetails: [{ unit: UNITS.percentage, decimalPlaces: 2 }],
        }));

        const settings =
            parametersSettings.length > 0
                ? parametersSettings
                : mappedParameters;

        return settings;
    };

    const updateClassificationParameters = (givenParams) => {
        const params = givenParams || parametersSettings;
        setClassificationParameters(
            params
                .filter(
                    (p) =>
                        p?.type?.value ===
                            MODEL_PROCESSING_TYPES.Classification?.value &&
                        p.name
                )
                .map((p) => {
                    return {
                        name: p.name,
                        classifier: getClassifierLabelsData(p.fakeId),
                        fakeId: p.fakeId,
                    };
                })
        );
    };

    const getClassifierLabelsData = (id) => {
        const classificationParam = classificationParameters?.find(
            (param) => param.fakeId === id
        );
        return classificationParam?.classifier ?? "";
    };

    const onSelectChange = (value, index) => {
        const newClassificationParameter = [...classificationParameters];
        let selectedClassifier = newClassificationParameter[index];
        newClassificationParameter[index] = {
            ...selectedClassifier,
            classifier: value,
        };
        setClassificationParameters(newClassificationParameter);
    };

    const getClassifierLabels = (classifierId) => {
        const labels = classifiersLookup.find(
            (classifier) => classifier.value === classifierId
        )?.classifierLabels;

        return labels.map((label) => `${label.label} - ${label.value}`);
    };

    const getManageClassifierSection = () => {
        return classificationParameters.map((parameter, index) => {
            return (
                <div className="flex flex-col mb-4" key={index}>
                    <div className="flex justify-between w-full mb-2">
                        <span className="text-primary-color-orange uppercase mr-2">
                            {parameter.name}
                        </span>
                        {parameter.classifier && (
                            <div>
                                <ProductTags
                                    tags={getClassifierLabels(
                                        parameter?.classifier?.value || ""
                                    )}
                                />
                            </div>
                        )}
                    </div>
                    <div className="w-full mr-2 col-span-3">
                        <Select
                            className="basic-single"
                            options={classifiersLookup}
                            value={parameter.classifier}
                            onChange={(value) => onSelectChange(value, index)}
                            styles={getReactSelectStyling()}
                            theme={getReactSelectTheme}
                        />
                    </div>
                    <ValidationError
                        errorMessage={
                            isFormSubmitted &&
                            classificationParamError[`${index}.classifier`]
                        }
                    />
                </div>
            );
        });
    };

    return (
        <>
            <div className="steps step4 show">
                <TitleWithDescription
                    title={t("manageModel")}
                    description={t("manageModelParagraph")}
                />
                <div className="card-table shadow mb-5">
                    <div className="upload-img-box flex">
                        <div className="relative">
                            {model.image && (
                                <span
                                    className="img-delete-icon"
                                    onClick={() => handleImageValue()}
                                >
                                    <i className="far fa-times-circle" />
                                </span>
                            )}
                            <div className="choose-img-wrapper">
                                <LazyImage
                                    src={
                                        model.image ||
                                        require("../../assets/images/neural.png")
                                            .default
                                    }
                                    alt="Model Image"
                                />
                                <div
                                    className="choose-img-btn flex"
                                    onClick={() => imageInput.current.click()}
                                >
                                    <i className="fas fa-plus-square"></i>
                                    <span className="ml-2 mt-2">
                                        <Trans i18nKey="upload">Upload</Trans>
                                    </span>
                                    <input
                                        type="file"
                                        name="image"
                                        accept="image/*"
                                        ref={imageInput}
                                        onChange={handleImage}
                                        className="hidden"
                                        multiple
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="main-customer-form-inputs w-1/2 w-full-mobile">
                            <div className="input-group">
                                <label
                                    htmlFor="input-1"
                                    className="uppercase required"
                                >
                                    <Trans i18nKey="modelName">
                                        Model Name
                                    </Trans>
                                </label>
                                <input
                                    type="text"
                                    placeholder={t("modelNamePlaceholder")}
                                    className="input-element"
                                    name="name"
                                    onChange={handleInputChange}
                                    value={model.name || ""}
                                    id="model-name"
                                />
                                {doesModelNameExist && isFormSubmitted ? (
                                    <>
                                        <div className="error-input-message flex items-center">
                                            <i className="fas fa-exclamation-triangle"></i>
                                            <span className="error-input-message-text">
                                                <Trans i18nKey="modelNameExists">
                                                    Model name already exists
                                                </Trans>
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    ""
                                )}
                                {isFormSubmitted && (
                                    <ValidationError
                                        errorMessage={error.name}
                                    />
                                )}
                            </div>
                            <div className="input-group">
                                <label htmlFor="input-2" className="uppercase">
                                    <Trans i18nKey="modelDescription">
                                        Model description
                                    </Trans>
                                </label>
                                <textarea
                                    placeholder={t("descriptionPlaceholder")}
                                    className="input-element textarea-element"
                                    name="description"
                                    onChange={handleInputChange}
                                    value={model.description || ""}
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </div>
                <TitleWithDescription
                    title={t("parametersSettings")}
                    description={t("parametersSettingsDescription")}
                />
                <div
                    className="card-table shadow mb-5"
                    id="parameter-settings-container"
                >
                    <ParametersForm
                        showAddParameter={isCustom}
                        parameters={parametersSettings}
                        setParameters={setParametersSettings}
                        error={parameterError}
                        isFormSubmitted={isFormSubmitted}
                        disableEditingName={!isCustom}
                        hideParameterDeleteIcon={!isCustom}
                        activeUnit={activeUnit}
                        setActiveUnit={setActiveUnit}
                        showClassificationOption={true}
                        updateClassificationParameters={
                            updateClassificationParameters
                        }
                        parameterUnitsMaps={parameterUnitsMaps}
                        isCustom={isCustom}
                    />
                </div>

                {!isEmpty(classificationParameters) &&
                    isAuthorized([ROLES.support], getRoles()) && (
                        <>
                            <TitleWithDescription
                                title={t("classificationParameters")}
                                description={t(
                                    "manageClassificationParameters"
                                )}
                            />
                            <div
                                className="card-table shadow mb-5"
                                id="parameter-settings-container"
                            >
                                {getManageClassifierSection()}
                            </div>
                        </>
                    )}

                <TitleWithDescription
                    title={t("scannerSettings")}
                    description={t("scannerSettingsDescription")}
                />
                <div className="card-table shadow mb-5">
                    <ScannerSettingsForm
                        scannerSettings={scannerSettings}
                        setScannerSettings={setScannerSettings}
                        isFormSubmitted={isFormSubmitted}
                        setIsFormValid={setIsScannerSettingsValid}
                    />
                </div>

                <TitleWithDescription
                    title={t("modelOptions")}
                    description={t("modelOptionsDescription")}
                />
                <div className="card-table shadow mb-5">
                    <div className="main-content-header">
                        <div className="main-customer-form-inputs">
                            <ModelSupportedFeatures
                                modelOptions={modelOptions}
                                setModelOptions={setModelOptions}
                                parametersSettings={parametersSettings}
                                activeUnitIndices={activeUnit}
                                errors={modelOptionsError}
                                isFormSubmitted={isFormSubmitted}
                            />
                        </div>
                    </div>
                </div>

                <TitleWithDescription
                    title={t("grantAccess")}
                    description={t("grantAccessMessage")}
                />
                <div className="card-table shadow mb-5">
                    <ModelAccess
                        createdBy={getUserEmail()}
                        selectedUsers={selectedUsers}
                        setSelectedUsers={setSelectedUsers}
                        mappedUserAccesses={mappedUserAccesses}
                        setMappedUserAccesses={setMappedUserAccesses}
                        selectedOrganizations={selectedOrganizations}
                        setSelectedOrganizations={setSelectedOrganizations}
                    />
                </div>
                {/* <div className="grid-3-cols">{renderRadioButtons()}</div> */}
            </div>
            <StepsNavButtons
                back={{
                    action: (e) => (isTesting ? prevStep(e, 3) : prevStep(e)),
                }}
                next={{
                    action: handleCreateModelClicked,
                    disabled: getIsStepValid(),
                    isSubmitting,
                    label: t("createdModel"),
                }}
            />
        </>
    );
};

export default ManageModel;
